import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import type { CheckInSearchResult, CheckInCount } from './types';

// --- Styles ---

const panelStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '80px',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  color: '#fff',
  margin: '0 0 8px 0',
};

const countBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  background: '#122b1c',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#c9a84c',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '44px',
  padding: '12px 14px',
  fontSize: '16px',
  border: '1px solid #2a4a3a',
  borderRadius: '8px',
  background: '#0d2418',
  color: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  marginBottom: '16px',
};

const resultCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 14px',
  background: '#122b1c',
  borderRadius: '8px',
  marginBottom: '8px',
};

const guestInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
};

const guestNameStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
};

const guestDetailStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#a0a0a0',
};

const statusBadgeBase: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  padding: '3px 8px',
  borderRadius: '4px',
  textTransform: 'uppercase',
};

const checkInBtnStyle: React.CSSProperties = {
  minWidth: '44px',
  minHeight: '44px',
  padding: '10px 16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  background: '#1a6b3c',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};

const checkInBtnDisabledStyle: React.CSSProperties = {
  ...checkInBtnStyle,
  background: '#2a4a3a',
  cursor: 'not-allowed',
  opacity: 0.6,
};

const errorToastStyle: React.CSSProperties = {
  padding: '10px 14px',
  background: '#5c1a1a',
  borderRadius: '8px',
  color: '#ffb3b3',
  fontSize: '13px',
  marginBottom: '12px',
};

const emptyStyle: React.CSSProperties = {
  textAlign: 'center',
  color: '#a0a0a0',
  fontSize: '14px',
  padding: '24px 0',
};

// --- Component ---

export function QuickCheckInPanel() {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CheckInSearchResult[]>([]);
  const [checkInCount, setCheckInCount] = useState<CheckInCount | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [checkingInSeatId, setCheckingInSeatId] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch check-in count on mount
  const fetchCount = useCallback(async () => {
    try {
      const count = await apiClient<CheckInCount>('/table-management/check-in/count');
      setCheckInCount(count);
    } catch {
      // Silently fail — count is supplementary info
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Manual search (no auto-debounce — user taps Search button)
  async function handleSearch() {
    const term = searchTerm.trim();
    if (term.length < 3) return;

    setIsSearching(true);
    setHasSearched(true);
    try {
      let param: string;
      if (/^[A-Za-z]{2,4}-?\d+$/.test(term)) {
        param = `ticketNumber=${encodeURIComponent(term)}`;
      } else if (/^\d+$/.test(term)) {
        param = `orderNumber=${encodeURIComponent(term)}`;
      } else {
        param = `name=${encodeURIComponent(term)}`;
      }

      const data = await apiClient<any>(
        `/table-management/check-in/search?${param}`,
      );
      // API returns { results: [...], totalResults: N } with guestName field
      const rawResults = Array.isArray(data) ? data : (data?.results || []);
      // Map API field names to frontend type
      const mapped: CheckInSearchResult[] = rawResults.map((r: any) => ({
        guestId: r.guestId,
        fullName: r.guestName || r.fullName || '',
        ticketNumber: r.ticketNumber || '',
        seatId: r.seatId || '',
        tableNumber: r.tableNumber || 0,
        seatNumber: r.seatNumber || 0,
        status: r.status || 'Assigned',
      }));
      setResults(mapped);
    } catch {
      setResults([]);
      setErrorToast('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }

  // Handle check-in action
  async function handleCheckIn(seatId: string) {
    setErrorToast(null);
    setCheckingInSeatId(seatId);

    try {
      await apiClient('/table-management/check-in', {
        method: 'POST',
        body: { seatId },
      });

      // Optimistically update local result status to CheckedIn
      setResults((prev) =>
        prev.map((r) => (r.seatId === seatId ? { ...r, status: 'CheckedIn' as const } : r)),
      );

      // Refresh count
      fetchCount();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setErrorToast(apiErr.message || 'Check-in failed. Please try again.');
    } finally {
      setCheckingInSeatId(null);
    }
  }

  // Dismiss error toast after 5 seconds
  useEffect(() => {
    if (!errorToast) return;
    const timer = setTimeout(() => setErrorToast(null), 5000);
    return () => clearTimeout(timer);
  }, [errorToast]);

  function getStatusBadgeStyle(status: CheckInSearchResult['status']): React.CSSProperties {
    const colors: Record<string, { bg: string; color: string }> = {
      CheckedIn: { bg: '#1a4a2e', color: '#6fcf97' },
      Assigned: { bg: '#2a3a1a', color: '#c9a84c' },
      Available: { bg: '#1a2a3a', color: '#6fb3cf' },
      Held: { bg: '#3a2a1a', color: '#cf9f6f' },
      Blocked: { bg: '#3a1a1a', color: '#cf6f6f' },
    };
    const c = colors[status] || colors.Assigned;
    return { ...statusBadgeBase, background: c.bg, color: c.color };
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}>Quick Check-In</h2>
        {checkInCount && (
          <div style={countBarStyle}>
            <span>✅ {checkInCount.checkedIn} / {checkInCount.totalAssigned} checked in</span>
            <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
              {checkInCount.totalAssigned > 0
                ? `${Math.round((checkInCount.checkedIn / checkInCount.totalAssigned) * 100)}%`
                : '0%'}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Name, order #, ticket #, or last name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          style={{ ...searchInputStyle, marginBottom: 0, flex: 1 }}
          aria-label="Search guests"
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={searchTerm.trim().length < 3 || isSearching}
          style={{
            padding: '12px 18px',
            fontSize: '15px',
            fontWeight: 700,
            backgroundColor: searchTerm.trim().length < 3 ? '#2a4a3a' : '#c9a84c',
            color: searchTerm.trim().length < 3 ? '#666' : '#0d2418',
            border: 'none',
            borderRadius: '8px',
            cursor: searchTerm.trim().length < 3 ? 'not-allowed' : 'pointer',
            minWidth: '80px',
            whiteSpace: 'nowrap' as const,
          }}
          aria-label="Search"
        >
          {isSearching ? '...' : '🔍 Search'}
        </button>
      </div>
      {searchTerm.trim().length > 0 && searchTerm.trim().length < 3 && (
        <div style={{ fontSize: '12px', color: '#c9a84c', marginBottom: '12px', marginTop: '-8px' }}>
          Type at least 3 characters to search
        </div>
      )}

      {errorToast && (
        <div style={errorToastStyle} role="alert">
          {errorToast}
        </div>
      )}

      {isSearching && (
        <div style={emptyStyle}>Searching...</div>
      )}

      {!isSearching && results.length === 0 && hasSearched && (
        <div style={emptyStyle}>No guests found. Try a different search.</div>
      )}

      {results.map((result) => (
        <div key={result.seatId} style={resultCardStyle}>
          <div style={guestInfoStyle}>
            <span style={guestNameStyle}>{result.fullName}</span>
            <span style={guestDetailStyle}>
              Table {result.tableNumber} · Seat {result.seatNumber}
            </span>
            <span style={getStatusBadgeStyle(result.status)}>{result.status}</span>
          </div>
          {result.status === 'Assigned' && (
            <button
              type="button"
              style={
                checkingInSeatId === result.seatId ? checkInBtnDisabledStyle : checkInBtnStyle
              }
              disabled={checkingInSeatId === result.seatId}
              onClick={() => handleCheckIn(result.seatId)}
              aria-label={`Check in ${result.fullName}`}
            >
              {checkingInSeatId === result.seatId ? '...' : 'Check In'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
